import { Component, type ErrorInfo, type ReactNode } from "react";

type RuntimeErrorPayload = {
  kind: string;
  message: string;
  stack?: string;
  componentStack?: string;
  url: string;
  userAgent: string;
  occurredAt: string;
};

function text(value: unknown) {
  if (value instanceof Error) return value.message;
  if (typeof value === "string") return value;
  try { return JSON.stringify(value); } catch { return String(value); }
}

function report(payload: Omit<RuntimeErrorPayload, "url" | "userAgent" | "occurredAt">) {
  const body: RuntimeErrorPayload = {
    ...payload,
    url: window.location.href,
    userAgent: navigator.userAgent,
    occurredAt: new Date().toISOString(),
  };
  fetch("/api/v1/client-logs", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => {});
}

export function installRuntimeErrorReporting() {
  window.addEventListener("error", event => report({
    kind: "window.error",
    message: event.message || text(event.error),
    stack: event.error instanceof Error ? event.error.stack : undefined,
  }));
  window.addEventListener("unhandledrejection", event => report({
    kind: "unhandledrejection",
    message: text(event.reason),
    stack: event.reason instanceof Error ? event.reason.stack : undefined,
  }));
}

export class RuntimeErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) { return { error }; }

  componentDidCatch(error: Error, info: ErrorInfo) {
    report({ kind: "react.render", message: error.message, stack: error.stack, componentStack: info.componentStack || undefined });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return <main style={{ maxWidth: 760, margin: "12vh auto", padding: 32, fontFamily: "system-ui", lineHeight: 1.7 }}>
      <h1>页面加载失败</h1>
      <p>浏览器异常已经发送到服务端日志，请刷新页面重试。</p>
      <pre style={{ whiteSpace: "pre-wrap", padding: 16, background: "#18181b", color: "#fafafa", borderRadius: 8 }}>{this.state.error.message}</pre>
      <button type="button" onClick={() => window.location.reload()} style={{ padding: "10px 18px", cursor: "pointer" }}>刷新页面</button>
    </main>;
  }
}
